import { userQueryKeys } from '@/config/queryKeys';
import { checkAndRedirectForOrgAdminAccess } from '@/helpers';
import { useAppStore } from '@/stores/app';
import type { UserPublic } from '@/types';
import { SUPER_ADMIN_ROLE } from '@/types';
import { createFileRoute, redirect } from '@tanstack/react-router';

// TODO: Re-enable OrgAdmin once GET /orgs/{id}/users returns results for
// non-SuperAdmin roles (same blocker as users list).
const allowedRoles = [SUPER_ADMIN_ROLE];

export const Route = createFileRoute('/users/$userId')({
  beforeLoad: ({ context, location }) => {
    const store = useAppStore.getState();
    const selectedOrganization = store.selectedOrganization || store.selectedOrgStub;
    checkAndRedirectForOrgAdminAccess({
      context,
      location,
      selectedOrganization,
      adminRoles: allowedRoles,
      redirect,
    });
  },
  loader: ({ context: { queryClient }, params: { userId } }) => {
    const store = useAppStore.getState();
    const orgId = store.selectedOrganization?.id ?? store.selectedOrgStub?.id ?? '';
    void queryClient.ensureQueryData({
      queryKey: [...userQueryKeys.detail(userId), orgId],
      // TODO: replace with apiClient.get<UserPublic>(userQueryKeys.endpoints.detail(userId))
      // when GET /users/{userId} endpoint lands.
      queryFn: (): Promise<UserPublic | null> => {
        const fromOrgCache = queryClient
          .getQueryData<UserPublic[]>(userQueryKeys.orgUsers(orgId))
          ?.find(u => u.id === userId);
        if (fromOrgCache) return Promise.resolve(fromOrgCache);
        // SA fallback: scan the query cache for any cached UserPublic[] that contains this user.
        const allEntries = queryClient.getQueriesData<UserPublic[]>({
          predicate: query => {
            const data = query.state.data;
            if (!Array.isArray(data) || data.length === 0) return false;
            const first = data[0];
            return (
              typeof first === 'object' &&
              first !== null &&
              'id' in first &&
              'email' in first &&
              'allowed_roles' in first
            );
          },
        });
        for (const [, data] of allEntries) {
          const found = data?.find(u => u.id === userId);
          if (found) return Promise.resolve(found);
        }
        return Promise.resolve(null);
      },
      staleTime: Infinity,
    });
  },
});
